import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { repository } from '../../data'
import { Modal } from '../kit'
import { fromLocalInput, problemOf, toLocalInput } from './time'

/**
 * Type a shift's times: fix one (usually a forgotten clock-out), or add one
 * that was never clocked. The report marks every shift saved here as typed in.
 *
 * Adding starts with **empty** fields rather than a plausible 08:00–16:30.
 * A default somebody saves without reading is a guessed number on a payslip,
 * which is the one thing this whole feature is built to avoid.
 */
export type ShiftEdit =
  | { readonly kind: 'edit'; readonly id: string; readonly startedAt: string; readonly endedAt: string | null; readonly who?: string | undefined }
  | { readonly kind: 'add'; readonly userId?: string | undefined; readonly who?: string | undefined }

export function ShiftEditor({ edit, onClose }: { edit: ShiftEdit; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient()
  const [start, setStart] = useState(edit.kind === 'edit' ? toLocalInput(edit.startedAt) : '')
  const [end, setEnd] = useState(edit.kind === 'edit' && edit.endedAt ? toLocalInput(edit.endedAt) : '')
  const [problem, setProblem] = useState('')

  const save = useMutation({
    mutationFn: async () => {
      const startedAt = fromLocalInput(start)
      const endedAt = fromLocalInput(end)
      if (!startedAt) throw new Error('When did the shift start?')
      if (!endedAt) throw new Error('When did it end?')
      return edit.kind === 'edit'
        ? repository.editShift(edit.id, { startedAt, endedAt })
        : repository.addShift({ startedAt, endedAt, userId: edit.userId })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['shifts'] })
      void queryClient.invalidateQueries({ queryKey: ['hours'] })
      onClose()
    },
    onError: (e) => setProblem(problemOf(e)),
  })

  return (
    <Modal
      title={edit.kind === 'edit' ? 'Fix this shift' : 'Add a shift'}
      subtitle={edit.who ? `${edit.who}’s hours. Saved times are marked as typed in on the report.` : 'Saved times are marked as typed in on the report.'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" disabled={save.isPending} onClick={() => save.mutate()}>
            Save
          </button>
        </>
      }
    >
      <div className="stack">
        <label className="field">
          <span className="field-label">Started</span>
          <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label className="field">
          <span className="field-label">Ended</span>
          <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
        {problem ? (
          <p className="critical" role="alert">
            {problem}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
